/**
 * Row ordering for the Factory schedule timeline — default / A→Z / Z→A, or a
 * planner's own custom order (like an Excel custom list).
 *
 * The mode and the custom order are remembered per grouping in localStorage
 * (`sch.factoryOrder.<grouping>`), so "per user" means per browser profile.
 * Storage can be unavailable (private window, blocked site data), so every
 * read and write is guarded and the view falls back to the default order.
 *
 * Only TOP-LEVEL rows are ordered (kitchens, lines, product rows, cleaners);
 * the rows nested under a header keep their own order.
 */

import { memo, useCallback, useMemo, useState } from 'react'
import { Box, Dialog, DialogContent, Popover } from '@mui/material'
import SwapVertIcon from '@mui/icons-material/SwapVert'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  FONT_MONO,
  FONT_SANS,
  PillButton,
  RADII,
  TOKENS,
} from './SchedulerDesign'

export type FactoryGrouping = 'kitchen' | 'flat' | 'combined' | 'cleaning'
export type RowSortMode = 'default' | 'asc' | 'desc' | 'custom'

/** One top-level row as the order control sees it. `id` is what the custom
 *  order stores, so it must be stable across runs (a name, not an index). */
export type OrderableRow = { id: string; label: string; detail?: string }

type StoredOrder = { mode: RowSortMode; order: string[] }

const DEFAULT_STORED: StoredOrder = { mode: 'default', order: [] }
const MODES: RowSortMode[] = ['default', 'asc', 'desc', 'custom']

const storageKey = (g: FactoryGrouping) => `sch.factoryOrder.${g}`

const readStored = (g: FactoryGrouping): StoredOrder => {
  try {
    const raw = window.localStorage.getItem(storageKey(g))
    if (!raw) return DEFAULT_STORED
    const p = JSON.parse(raw)
    // Validated field by field: a hand-edited or older value must degrade to
    // the default order, never break the timeline.
    const mode: RowSortMode = MODES.includes(p?.mode) ? p.mode : 'default'
    const order: string[] = Array.isArray(p?.order)
      ? p.order.filter((v: unknown): v is string => typeof v === 'string')
      : []
    return { mode, order }
  } catch {
    return DEFAULT_STORED
  }
}

const writeStored = (g: FactoryGrouping, s: StoredOrder) => {
  try {
    window.localStorage.setItem(storageKey(g), JSON.stringify(s))
  } catch {
    // Unavailable storage only costs the preference surviving a reload.
  }
}

/** The sort mode and custom order for one grouping, persisted per grouping. */
export const useFactoryRowOrder = (grouping: FactoryGrouping) => {
  // Cached in state so a change re-renders; read straight from storage the
  // first time a grouping is shown, so switching grouping never renders one
  // frame in the wrong order (which an effect-based load would).
  const [cache, setCache] = useState<
    Partial<Record<FactoryGrouping, StoredOrder>>
  >({})
  const current = useMemo(
    () => cache[grouping] ?? readStored(grouping),
    [cache, grouping]
  )
  const update = useCallback(
    (next: StoredOrder) => {
      writeStored(grouping, next)
      setCache((c) => ({ ...c, [grouping]: next }))
    },
    [grouping]
  )
  const setMode = useCallback(
    (mode: RowSortMode) => update({ ...current, mode }),
    [current, update]
  )
  const saveCustom = useCallback(
    (order: string[]) => update({ mode: 'custom', order }),
    [update]
  )
  return { mode: current.mode, order: current.order, setMode, saveCustom }
}

const labelCompare = (a: string, b: string) =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })

/** Orders `items` by `mode`. Rows absent from a custom order go after the
 *  ordered ones, in their default order (the sort is stable). */
export function applyRowOrder<T>(
  items: T[],
  idOf: (t: T) => string,
  labelOf: (t: T) => string,
  mode: RowSortMode,
  order: string[]
): T[] {
  if (mode === 'asc' || mode === 'desc') {
    const dir = mode === 'asc' ? 1 : -1
    return items
      .slice()
      .sort((a, b) => dir * labelCompare(labelOf(a), labelOf(b)))
  }
  if (mode === 'custom' && order.length) {
    const rank = new Map(order.map((id, i) => [id, i]))
    const at = (t: T) => rank.get(idOf(t)) ?? Number.MAX_SAFE_INTEGER
    return items.slice().sort((a, b) => at(a) - at(b))
  }
  return items
}

const MODE_LABEL: Record<RowSortMode, string> = {
  default: 'Default order',
  asc: 'A → Z',
  desc: 'Z → A',
  custom: 'Custom',
}

// ─── Toolbar control ─────────────────────────────────────────────────────────

export const FactoryRowOrderControl = memo(
  ({
    mode,
    onModeChange,
    rows,
    customOrder,
    onSaveCustom,
    rowNoun,
  }: {
    mode: RowSortMode
    onModeChange: (m: RowSortMode) => void
    /** Top-level rows of the current grouping, in their DEFAULT order. */
    rows: OrderableRow[]
    customOrder: string[]
    onSaveCustom: (order: string[]) => void
    /** What a row is in this grouping ("kitchens", "lines"…), for the dialog. */
    rowNoun: string
  }) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const [dialogOpen, setDialogOpen] = useState(false)
    const active = mode !== 'default'

    const choose = (m: RowSortMode) => {
      setAnchorEl(null)
      if (m === 'custom') setDialogOpen(true)
      else onModeChange(m)
    }

    return (
      <>
        <Box
          component="button"
          title="Sort rows"
          onClick={(e: React.MouseEvent<HTMLElement>) =>
            setAnchorEl(anchorEl ? null : e.currentTarget)
          }
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            height: 24,
            px: '8px',
            border: `1px solid ${active || anchorEl ? TOKENS.stateComplete : TOKENS.line300}`,
            borderRadius: RADII.sm,
            bgcolor: active ? 'rgba(14,124,134,0.08)' : '#fff',
            cursor: 'pointer',
            fontSize: 10.5,
            fontWeight: 600,
            fontFamily: FONT_SANS,
            color: active ? TOKENS.stateComplete : TOKENS.ink500,
            whiteSpace: 'nowrap',
          }}
        >
          <SwapVertIcon sx={{ fontSize: 14 }} />
          {active ? MODE_LABEL[mode] : 'Sort'}
          <ExpandMoreIcon
            sx={{
              fontSize: 14,
              transition: 'transform 0.15s',
              transform: anchorEl ? 'rotate(180deg)' : 'none',
            }}
          />
        </Box>
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{
            sx: {
              mt: '6px',
              width: 190,
              borderRadius: RADII.sm,
              border: `1px solid ${TOKENS.line200}`,
              boxShadow:
                '0 12px 32px rgba(14,27,34,0.18), 0 2px 8px rgba(14,27,34,0.08)',
              py: '4px',
            },
          }}
        >
          {MODES.map((m) => (
            <Box
              key={m}
              component="button"
              onClick={() => choose(m)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                width: '100%',
                height: 30,
                px: '12px',
                border: 'none',
                bgcolor: 'transparent',
                cursor: 'pointer',
                textAlign: 'left',
                fontSize: 11.5,
                fontWeight: mode === m ? 700 : 500,
                fontFamily: FONT_SANS,
                color: TOKENS.ink900,
                '&:hover': { bgcolor: TOKENS.surface50 },
              }}
            >
              <Box sx={{ width: 14, display: 'flex', flexShrink: 0 }}>
                {mode === m && (
                  <CheckIcon
                    sx={{ fontSize: 13, color: TOKENS.stateComplete }}
                  />
                )}
              </Box>
              {m === 'custom'
                ? customOrder.length
                  ? 'Custom order — edit…'
                  : 'Custom order…'
                : MODE_LABEL[m]}
            </Box>
          ))}
        </Popover>
        {/* Mounted only while open so the list is re-seeded from the current
            rows and saved order every time it opens. */}
        {dialogOpen && (
          <CustomOrderDialog
            rows={rows}
            customOrder={customOrder}
            rowNoun={rowNoun}
            onCancel={() => setDialogOpen(false)}
            onSave={(order) => {
              onSaveCustom(order)
              setDialogOpen(false)
            }}
          />
        )}
      </>
    )
  }
)
FactoryRowOrderControl.displayName = 'FactoryRowOrderControl'

// ─── Custom order dialog ─────────────────────────────────────────────────────

const iconBtnSx = (enabled: boolean) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 22,
  height: 22,
  p: 0,
  border: `1px solid ${TOKENS.line300}`,
  borderRadius: RADII.sm,
  bgcolor: '#fff',
  color: TOKENS.ink500,
  cursor: enabled ? 'pointer' : 'not-allowed',
  opacity: enabled ? 1 : 0.35,
  '&:hover': enabled ? { bgcolor: TOKENS.tealTint } : {},
})

const CustomOrderDialog = ({
  rows,
  customOrder,
  rowNoun,
  onCancel,
  onSave,
}: {
  rows: OrderableRow[]
  customOrder: string[]
  rowNoun: string
  onCancel: () => void
  onSave: (order: string[]) => void
}) => {
  const [list, setList] = useState<OrderableRow[]>(() =>
    applyRowOrder(
      rows,
      (r) => r.id,
      (r) => r.label,
      'custom',
      customOrder
    )
  )
  const [dragIdx, setDragIdx] = useState<number | null>(null)

  const move = (from: number, to: number) =>
    setList((l) => {
      if (to < 0 || to >= l.length || from === to) return l
      const next = l.slice()
      const [item] = next.splice(from, 1)
      next.splice(to, 0, item)
      return next
    })

  const save = () => {
    // Rows saved earlier but not on screen now (filtered out, or absent this
    // week) keep their place after the visible ones rather than being
    // forgotten by a save made while a filter was on.
    const shown = new Set(list.map((r) => r.id))
    onSave([
      ...list.map((r) => r.id),
      ...customOrder.filter((id) => !shown.has(id)),
    ])
  }

  return (
    <Dialog
      open
      onClose={onCancel}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 'min(460px, 94vw)',
          border: `1px solid ${TOKENS.line300}`,
          borderRadius: '3px',
          m: '24px',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '11px',
          px: '18px',
          py: '15px',
          borderBottom: `1px solid ${TOKENS.line200}`,
          bgcolor: TOKENS.surface0,
        }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: '2px',
            bgcolor: TOKENS.tealTint,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <SwapVertIcon sx={{ fontSize: 18, color: TOKENS.stateComplete }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              fontSize: 15,
              fontWeight: 700,
              color: TOKENS.ink900,
              letterSpacing: '-0.01em',
              fontFamily: FONT_SANS,
            }}
          >
            Custom order
          </Box>
          <Box
            sx={{ fontSize: 11.5, color: TOKENS.ink400, fontFamily: FONT_SANS }}
          >
            Drag or use the arrows to order the {rowNoun}. Saved in this
            browser.
          </Box>
        </Box>
        <Box
          component="button"
          onClick={onCancel}
          sx={{
            border: 'none',
            bgcolor: 'transparent',
            cursor: 'pointer',
            p: '4px',
            borderRadius: '2px',
            display: 'flex',
            '&:hover': { bgcolor: TOKENS.surface50 },
          }}
        >
          <CloseIcon sx={{ fontSize: 18, color: TOKENS.ink400 }} />
        </Box>
      </Box>

      {/* Body */}
      <DialogContent sx={{ p: '10px 18px', bgcolor: TOKENS.surface50 }}>
        {list.length === 0 ? (
          <Box
            sx={{
              py: '20px',
              textAlign: 'center',
              fontSize: 11.5,
              color: TOKENS.ink400,
              fontFamily: FONT_SANS,
            }}
          >
            No {rowNoun} to order.
          </Box>
        ) : (
          <Box
            sx={{
              maxHeight: 380,
              overflowY: 'auto',
              border: `1px solid ${TOKENS.line200}`,
              borderRadius: RADII.sm,
              bgcolor: TOKENS.surface0,
            }}
          >
            {list.map((r, i) => (
              <Box
                key={r.id}
                draggable
                onDragStart={() => setDragIdx(i)}
                onDragOver={(e: React.DragEvent) => {
                  e.preventDefault()
                  if (dragIdx != null && dragIdx !== i) {
                    move(dragIdx, i)
                    setDragIdx(i)
                  }
                }}
                onDragEnd={() => setDragIdx(null)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  height: 34,
                  px: '8px',
                  borderBottom:
                    i === list.length - 1
                      ? 'none'
                      : `1px solid ${TOKENS.line100}`,
                  bgcolor: dragIdx === i ? TOKENS.tealTint : 'transparent',
                  cursor: 'grab',
                }}
              >
                <DragIndicatorIcon
                  sx={{ fontSize: 15, color: TOKENS.ink300, flexShrink: 0 }}
                />
                <Box
                  sx={{
                    width: 22,
                    flexShrink: 0,
                    fontFamily: FONT_MONO,
                    fontSize: 10,
                    color: TOKENS.ink400,
                    textAlign: 'right',
                  }}
                >
                  {i + 1}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, fontFamily: FONT_SANS }}>
                  <Box
                    sx={{
                      fontSize: 11.5,
                      fontWeight: 600,
                      color: TOKENS.ink900,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {r.label}
                  </Box>
                  {r.detail && (
                    <Box
                      sx={{
                        fontSize: 9.5,
                        color: TOKENS.ink400,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {r.detail}
                    </Box>
                  )}
                </Box>
                <Box
                  component="button"
                  title="Move up"
                  disabled={i === 0}
                  onClick={() => move(i, i - 1)}
                  sx={iconBtnSx(i > 0)}
                >
                  <ArrowUpwardIcon sx={{ fontSize: 13 }} />
                </Box>
                <Box
                  component="button"
                  title="Move down"
                  disabled={i === list.length - 1}
                  onClick={() => move(i, i + 1)}
                  sx={iconBtnSx(i < list.length - 1)}
                >
                  <ArrowDownwardIcon sx={{ fontSize: 13 }} />
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      {/* Footer */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          px: '18px',
          py: '12px',
          borderTop: `1px solid ${TOKENS.line200}`,
          bgcolor: TOKENS.surface0,
        }}
      >
        <PillButton
          kind="subtle"
          onClick={() => setList(rows)}
          title="Put the rows back in the default order"
        >
          Reset
        </PillButton>
        <Box sx={{ flex: 1 }} />
        <PillButton kind="ghost" onClick={onCancel}>
          Cancel
        </PillButton>
        <PillButton kind="primary" onClick={save} disabled={!list.length}>
          Save order
        </PillButton>
      </Box>
    </Dialog>
  )
}
